import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import {
  LoadSource,
  LoadStatus,
  TRUCK_DEFAULTS,
  canTransition,
  legalCheck,
  newId,
  parseDim,
  parseWeight,
  toCents,
  type Load,
  type Place,
} from "@haulsheet/core";
import { SetupCard } from "../components/check/SetupCard";
import { linkBrokerByName } from "../loads/broker";
import { localDateIso } from "../logs/fuel";
import { loadActiveContract } from "../db/contracts";
import {
  BoardPicker,
  type BoardRow,
} from "../components/check/BoardPicker";
import { LegalCheckList } from "../components/check/LegalCheckList";
import {
  EMPTY_LOAD_FORM,
  LoadForm,
  type LoadFormValues,
} from "../components/check/LoadForm";
import { OcrReview } from "../components/check/OcrReview";
import { PayBreakdownTable } from "../components/check/PayBreakdownTable";
import {
  QuoteSourcePicker,
  type QuoteSource,
} from "../components/check/QuoteSourcePicker";
import { VerdictCard } from "../components/check/VerdictCard";
import { Button } from "../components/ui/Button";
import { getDb } from "../db/dexie";
import { buildAnalyzeInput } from "../hooks/useAnalyzeLoad";
import { analyze } from "@haulsheet/core";
import { runOcr } from "../ocr/runOcr";
import { nextStepForOcr } from "../ocr/toForm";
import { useAuthStore } from "../store/authStore";
import { getSyncClient } from "../sync/SyncClient";

type Flow =
  | "idle"
  | "reading"
  | "reviewing"
  | "pickingFromBoard"
  | "editing";

function emptyPlace(): Place {
  return { text: "", city: null, state: null, zip: null, lat: null, lng: null };
}

function placeFromText(text: string): Place {
  return { ...emptyPlace(), text: text.trim() };
}

function formToPartial(values: LoadFormValues) {
  const linehaul = values.linehaulDollars.trim()
    ? toCents(Number(values.linehaulDollars))
    : 0;
  const fscCentsPerMile = values.fscPerMileDollars.trim()
    ? Math.round(Number(values.fscPerMileDollars) * 100)
    : 0;
  return {
    origin: placeFromText(values.originText),
    destination: placeFromText(values.destinationText),
    linehaulCents: Number.isFinite(linehaul) ? linehaul : 0,
    fscCentsPerMile: Number.isFinite(fscCentsPerMile) ? fscCentsPerMile : 0,
    accessorialsCents: values.accessorialsDollars.trim()
      ? toCents(Number(values.accessorialsDollars))
      : 0,
    loadedMiles: values.loadedMiles.trim()
      ? Number(values.loadedMiles)
      : 0,
    deadheadMiles: values.deadheadMiles.trim()
      ? Number(values.deadheadMiles)
      : 0,
    weightLb: values.weight.trim() ? parseWeight(values.weight) : null,
    widthIn: values.width.trim() ? parseDim(values.width) : null,
    heightIn: values.height.trim() ? parseDim(values.height) : null,
    lengthIn: values.length.trim() ? parseDim(values.length) : null,
    note: values.note.trim() || null,
    refNumber: values.refNumber.trim() || null,
    loadDate: /^\d{4}-\d{2}-\d{2}$/.test(values.loadDate) ? values.loadDate : null,
  };
}

function loadToForm(load: Load, brokerName: string): LoadFormValues {
  return {
    originText: load.origin.text ?? "",
    destinationText: load.destination.text ?? "",
    linehaulDollars: load.linehaulCents ? (load.linehaulCents / 100).toString() : "",
    fscPerMileDollars: load.fscCentsPerMile
      ? (load.fscCentsPerMile / 100).toString()
      : "",
    accessorialsDollars: load.accessorialsCents
      ? (load.accessorialsCents / 100).toString()
      : "",
    loadedMiles: load.loadedMiles ? String(load.loadedMiles) : "",
    deadheadMiles: load.deadheadMiles ? String(load.deadheadMiles) : "",
    weight: load.weightLb != null ? String(load.weightLb) : "",
    width: load.widthIn != null ? String(load.widthIn) : "",
    height: load.heightIn != null ? String(load.heightIn) : "",
    length: load.lengthIn != null ? String(load.lengthIn) : "",
    note: load.note ?? "",
    brokerName,
    refNumber: load.refNumber ?? "",
    loadDate: load.loadDate ?? "",
  };
}

export function CheckScreen() {
  const { loadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const me = useAuthStore((s) => s.me);
  const fileRef = useRef<HTMLInputElement>(null);
  const today = () => localDateIso(me?.org.timezone);
  const emptyForm = (): LoadFormValues => ({ ...EMPTY_LOAD_FORM, loadDate: today() });
  /** Form carried over from /check → /check/:id after the first keystroke created the row. */
  const carried = (location.state as { form?: LoadFormValues } | null)?.form;
  const [flow, setFlow] = useState<Flow>(loadId ? "editing" : "idle");
  const [form, setForm] = useState<LoadFormValues>(() => carried ?? emptyForm());
  /** Hydrate the form from the stored load once per screen, not on every live update (that wiped fields not yet saved). */
  const hydratedRef = useRef(Boolean(carried));
  const [localLoadId, setLocalLoadId] = useState<string | null>(loadId ?? null);
  const [ocrFields, setOcrFields] = useState<Record<string, unknown>>({});
  const [ocrForm, setOcrForm] = useState<{ form: LoadFormValues; source: LoadSource } | null>(null);
  const [boardRows, setBoardRows] = useState<BoardRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const createdRef = useRef(false);

  const existing = useLiveQuery(
    async () => {
      if (!localLoadId) return null;
      return getDb().load.get(localLoadId);
    },
    [localLoadId],
  );

  const contract = useLiveQuery(() => loadActiveContract(), []);

  const cost = useLiveQuery(async () => {
    const db = getDb();
    if (me?.driverId) {
      const byDriver = await db.cost_profile
        .where("driverId")
        .equals(me.driverId)
        .first();
      if (byDriver) return byDriver;
    }
    return db.cost_profile.filter((c) => c.deletedAt == null).first();
  }, [me?.driverId]);

  const truck = useLiveQuery(async () => {
    return getDb().truck.filter((t) => t.deletedAt == null).first();
  }, []);

  // A9: open load is always the URL loadId — sync local state when it changes.
  useEffect(() => {
    setLocalLoadId(loadId ?? null);
    createdRef.current = false;
    if (!loadId) {
      setForm(emptyForm());
      setFlow("idle");
      setOcrFields({});
      setBoardRows([]);
      setError(null);
    }
  }, [loadId]);

  useEffect(() => {
    if (!existing || !loadId || existing.id !== loadId || hydratedRef.current) return;
    hydratedRef.current = true;
    let cancelled = false;
    void (async () => {
      const broker = existing.brokerId ? await getDb().broker.get(existing.brokerId) : undefined;
      if (cancelled) return;
      setForm(loadToForm(existing, broker?.name ?? ""));
      setFlow("editing");
    })();
    return () => {
      cancelled = true;
    };
  }, [existing, loadId]);

  const pay = useMemo(() => {
    const partial = formToPartial(form);
    return analyze(
      buildAnalyzeInput(
        {
          ...partial,
          routeMinutes: existing?.routeMinutes ?? null,
          routeDeadheadMinutes: existing?.routeDeadheadMinutes ?? null,
        },
        contract,
        cost,
        truck,
      ),
    );
  }, [form, contract, cost, truck, existing]);

  const legal = useMemo(() => {
    const partial = formToPartial(form);
    return legalCheck({
      widthIn: partial.widthIn,
      heightIn: partial.heightIn,
      lengthIn: partial.lengthIn,
      weightLb: partial.weightLb,
      tareLb: truck?.tareLb ?? TRUCK_DEFAULTS.tareLb,
      trailerHint: existing?.trailerType ?? null,
    });
  }, [form, truck, existing]);

  async function ensureLocalRow(
    values: LoadFormValues,
    source: LoadSource = LoadSource.typed,
  ): Promise<string> {
    if (localLoadId) {
      await persistPatch(localLoadId, values, source);
      return localLoadId;
    }
    if (!me) throw new Error("Not signed in");
    const id = newId();
    const now = new Date().toISOString();
    const partial = formToPartial(values);
    const row: Load = {
      id,
      orgId: me.org.id,
      version: 0,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
      driverId: me.driverId,
      status: LoadStatus.considering,
      source,
      brokerId: null,
      brokerContactId: null,
      payContractId: contract?.id ?? null,
      refNumber: partial.refNumber,
      loadDate: partial.loadDate ?? today(),
      origin: partial.origin,
      destination: partial.destination,
      linehaulCents: partial.linehaulCents,
      fscCentsPerMile: partial.fscCentsPerMile,
      accessorialsCents: partial.accessorialsCents,
      loadedMiles: partial.loadedMiles,
      deadheadMiles: partial.deadheadMiles,
      weightLb: partial.weightLb,
      widthIn: partial.widthIn,
      heightIn: partial.heightIn,
      lengthIn: partial.lengthIn,
      trailerType: null,
      note: partial.note,
      bookedAt: null,
      deliveredAt: null,
      paidAt: null,
      settlementId: null,
      routeMinutes: null,
      routeDeadheadMinutes: null,
    };
    await getSyncClient().mutate({
      entity: "load",
      op: "upsert",
      id,
      baseVersion: null,
      patch: row as unknown as Record<string, unknown>,
      row: row as unknown as Record<string, unknown>,
    });
    setLocalLoadId(id);
    createdRef.current = true;
    navigate(`/check/${id}`, { replace: true, state: { form: values } });
    return id;
  }

  async function persistPatch(
    id: string,
    values: LoadFormValues,
    source?: LoadSource,
  ) {
    const current = await getDb().load.get(id);
    if (!current) return;
    const partial = formToPartial(values);
    const payContractId = current.payContractId ?? contract?.id ?? null;
    const next: Load = {
      ...current,
      ...partial,
      source: source ?? current.source,
      payContractId,
      updatedAt: new Date().toISOString(),
    };
    // The contract may have loaded after the row was created: send it too,
    // or the server's load has none and refuses to book it.
    const patch: Record<string, unknown> = { ...partial };
    if (payContractId !== current.payContractId) patch.payContractId = payContractId;
    await getSyncClient().mutate({
      entity: "load",
      op: "upsert",
      id,
      baseVersion: current.version || null,
      patch,
      row: next as unknown as Record<string, unknown>,
    });
  }

  async function onFormChange(next: LoadFormValues) {
    setForm(next);
    setFlow("editing");
    // The defaulted date alone doesn't make a draft.
    const anyFilled = Object.entries(next).some(([k, v]) => k !== "loadDate" && v.trim() !== "");
    if (!anyFilled) return;
    try {
      await ensureLocalRow(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    }
  }

  async function onPickSource(source: QuoteSource) {
    setError(null);
    if (source === "type") {
      setFlow("editing");
      return;
    }
    if (source === "board") {
      setFlow("pickingFromBoard");
      return;
    }
    fileRef.current?.click();
  }

  async function onFile(file: File) {
    setFlow("reading");
    setError(null);
    const result = await runOcr(file);
    setOcrFields(result.parsed.fields);
    if (result.reason) {
      const msg =
        result.reason === "timeout"
          ? "Reading the image took too long — type the load instead."
          : result.reason === "empty"
            ? "No text found — type the load instead."
            : "Could not open that image — type the load instead.";
      setFlow("editing");
      setError(msg);
      return;
    }
    const next = nextStepForOcr(result.parsed);
    if (next.kind === "pick") {
      setBoardRows(next.rows);
      setFlow("pickingFromBoard");
    } else if (next.kind === "review") {
      setOcrForm({ form: next.form, source: next.source });
      setFlow("reviewing");
    } else if (next.kind === "fuel") {
      setOcrForm(null);
      setFlow("reviewing");
    } else {
      setFlow("editing");
      setError("Could not read that image — type the load instead.");
    }
  }

  /** Accept / Edit on OCR review: put the read values on the form and save the draft. */
  async function applyOcrForm() {
    if (!ocrForm) {
      setFlow("editing");
      return;
    }
    setForm(ocrForm.form);
    setFlow("editing");
    try {
      await ensureLocalRow(ocrForm.form, ocrForm.source);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    }
    setOcrForm(null);
  }

  async function commitBroker(name: string) {
    try {
      const hasOther = Object.entries(form).some(([k, v]) => k !== "loadDate" && v.trim() !== "");
      if (!localLoadId && !hasOther) return;
      const id = localLoadId ?? (await ensureLocalRow(form));
      await linkBrokerByName(id, name);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    }
  }

  async function book() {
    setError(null);
    if (!me) return;
    const id = await ensureLocalRow(form);
    await linkBrokerByName(id, form.brokerName);
    const load = await getDb().load.get(id);
    if (!load) return;
    const result = canTransition(load, LoadStatus.booked, {
      role: me.role,
      actorDriverId: me.driverId,
    });
    if (!result.ok) {
      setError(result.message);
      return;
    }
    const now = new Date().toISOString();
    const next: Load = {
      ...load,
      status: LoadStatus.booked,
      bookedAt: now,
      updatedAt: now,
    };
    await getSyncClient().mutate({
      entity: "load",
      op: "upsert",
      id,
      baseVersion: load.version || null,
      // Re-send the contract with the booking: the server checks it too.
      patch: load.payContractId
        ? { status: LoadStatus.booked, payContractId: load.payContractId }
        : { status: LoadStatus.booked },
      row: next as unknown as Record<string, unknown>,
    });
    navigate(`/loads/${id}`);
  }

  async function pass() {
    setError(null);
    if (!me) return;
    const id = await ensureLocalRow(form);
    await linkBrokerByName(id, form.brokerName);
    const load = await getDb().load.get(id);
    if (!load) return;
    const result = canTransition(load, LoadStatus.passed, {
      role: me.role,
      actorDriverId: me.driverId,
    });
    if (!result.ok) {
      setError(result.message);
      return;
    }
    const next: Load = {
      ...load,
      status: LoadStatus.passed,
      updatedAt: new Date().toISOString(),
    };
    await getSyncClient().mutate({
      entity: "load",
      op: "upsert",
      id,
      baseVersion: load.version || null,
      patch: { status: LoadStatus.passed },
      row: next as unknown as Record<string, unknown>,
    });
    navigate("/loads");
  }

  return (
    <div className="stack">
      <h1 className="screen-title">Check</h1>
      {!loadId && flow === "idle" ? <SetupCard /> : null}
      {flow === "idle" || flow === "editing" || flow === "reading" ? (
        <QuoteSourcePicker
          disabled={flow === "reading"}
          onPick={(s) => void onPickSource(s)}
        />
      ) : null}
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onFile(f);
          e.target.value = "";
        }}
      />
      {flow === "reading" ? (
        <div className="card muted">Reading screenshot…</div>
      ) : null}
      {flow === "reviewing" ? (
        <OcrReview
          fields={ocrFields}
          form={ocrForm?.form ?? null}
          onAccept={() => void applyOcrForm()}
          onEdit={() => void applyOcrForm()}
          onCancel={() => {
            setOcrForm(null);
            setFlow("idle");
            setForm(EMPTY_LOAD_FORM);
          }}
        />
      ) : null}
      {flow === "pickingFromBoard" ? (
        <BoardPicker
          rows={boardRows}
          onPick={(row) => {
            const next = row.form ?? {
              ...EMPTY_LOAD_FORM,
              originText: row.origin,
              destinationText: row.destination,
              linehaulDollars: String(row.linehaulDollars),
              loadedMiles: String(row.miles),
            };
            setForm(next);
            setFlow("editing");
            void ensureLocalRow(next, LoadSource.screenshot);
          }}
          onCancel={() => setFlow("idle")}
        />
      ) : null}
      {(flow === "editing" || flow === "idle") ? (
        <>
          <LoadForm
            values={form}
            onChange={(v) => void onFormChange(v)}
            onBrokerCommit={(name) => void commitBroker(name)}
          />
          <VerdictCard verdict={pay.verdict} />
          <PayBreakdownTable pay={pay} />
          <LegalCheckList result={legal} />
          <div className="row">
            <Button variant="primary" onClick={() => void book()}>
              Book
            </Button>
            <Button variant="danger" onClick={() => void pass()}>
              Pass
            </Button>
          </div>
        </>
      ) : null}
      {error ? <p className="legal-flag">{error}</p> : null}
    </div>
  );
}
